<script>
  import dayjs from 'dayjs';
  import CustomParseFormat from 'dayjs/plugin/customParseFormat';
  import { afterUpdate, onMount } from 'svelte';
  import { fade } from 'svelte/transition';
  import { FOUR_DIGITS_YEAR_DATE_FORMAT } from './../../../components/availability/availability-datepicker-config';
  import AvailabilityStatus from './component/AvailabilityStatus.svelte';
  import DateRangerPicker from './component/DateRangerPicker.svelte';
  import GuestPicker from './component/GuestPicker.svelte';
  import ListingResult from './component/ListingResult.svelte';
  import ToggleVisibility from './component/ToggleVisibility.svelte';
  import {
    availabilityData,
    availabilityLocationTypes,
    fetchStates,
    getAvailabilityList,
    isFetchingAvailability,
    isLoadingAvailabilityData,
    isRejectedAvailabilityData,
    isResolvedAvailabilityData,
  } from './store/fetchAvailabilityData.js';
  import {
    availabilityEnableDateMessageStatus,
    availabilityEnableDateStatus,
    availabilityEnablesDatesData,
    isAllBooked,
    isAllTooManyPeople,
    getAvailabilityEnableDates
  } from './store/fetchAvailabilityEnableDates.js';
  import {
    adultsValue,
    availabilityWindowWidth,
    childrenValue,
    dateFrom,
    datesQuery,
    dateTo,
    guestsQuery,
    infantsValue,
    isDateParametersValid,
    isLoadingSkeleton,
    isTriggerCheckAvailabilityState,
    numberOfGuest,
    scContextItemId,
    scContextItemIdQuery,
    scFormToken,
    scFormTokenQuery,
  } from './store/store.js';
  import { getUrlParameter, sleep } from './util';

  dayjs.extend(CustomParseFormat);

  export let sitecoreContextItemId;
  export let sitecoreFormToken;
  export let elementShadow;

  let isHidden = true;
  let isFromVisitorInfoPage = false;
  let windowWidth;
  let isFocusOnPanelToggle = false;

  $: availabilityWindowWidth.set(windowWidth);
  $: scContextItemId.set(sitecoreContextItemId);
  $: scFormToken.set(sitecoreFormToken);

  // URL query string
  $: urlQueryString = `?${$datesQuery}${$guestsQuery}`;

  // Request query string
  $: endPointQueryString = `${$scContextItemIdQuery}${$datesQuery}${$guestsQuery}&${$scFormTokenQuery}`;

  // State for button and request ajax
  $: disabled = !(
    $numberOfGuest &&
    dayjs($dateFrom, FOUR_DIGITS_YEAR_DATE_FORMAT).isValid() &&
    dayjs($dateTo, FOUR_DIGITS_YEAR_DATE_FORMAT).isValid()
  );

  $: isEmptyLocationTypes =
    $availabilityData.hasOwnProperty('LocationTypes') &&
    $availabilityLocationTypes.length === 0;

  $: if ($isTriggerCheckAvailabilityState) {
    // isFocusOnPanelToggle focus on the panel toggle button
    openPanel();
    isFocusOnPanelToggle = true;
  }

  $: onLoadWithParameters =
    $isDateParametersValid && $isLoadingAvailabilityData;

  $: if (
    isFromVisitorInfoPage ||
    $isTriggerCheckAvailabilityState ||
    $isResolvedAvailabilityData ||
    $isRejectedAvailabilityData ||
    $isAllTooManyPeople
  ) {
    openPanel();
  }

  function openPanel() {
    isLoadingSkeleton.set(false);
    isHidden = false;
  }

  // Clear all input data and query string
  function clearAll() {
    console.log("Clear all");
    dateFrom.set('');
    dateTo.set('');
    adultsValue.set(1);
    childrenValue.set(0);
    infantsValue.set(0);
    isFetchingAvailability.set(fetchStates.IDLE);
    availabilityEnablesDatesData.set({});
    availabilityLocationTypes.set([]);
    availabilityData.set({});
    isDateParametersValid.set(false);
    history.pushState(
      null,
      null,
      window.location.href.replace(window.location.search, '')
    );
    reloadBaseData();
  }
  async function reloadBaseData() {
    await sleep(500);
    getAvailabilityEnableDates(endPointQueryString);
  }

  async function onLoadCallback() {
    await sleep(500);
    getAvailabilityList(endPointQueryString);
  }

  // Submit handle for updating URL and request ajax
  function formHandleSubmit() {
    history.pushState(null, null, `${urlQueryString}`);
    getAvailabilityList(endPointQueryString);
  }

  onMount(() => {
    // Hide skeleton button
    elementShadow.classList.add('d-none');

    // Query from Visitor Info page
    isFromVisitorInfoPage =
      getUrlParameter(window.location.search, 'ref') === 'visitor-info';
  });

  afterUpdate(() => {
    // Reset focus state for child toggle
    isFocusOnPanelToggle = false;
  });
</script>

<svelte:window
  bind:innerWidth={windowWidth}
  on:load={$isDateParametersValid && onLoadCallback()} />

<ToggleVisibility
  elementId="availability"
  label="Search availability"
  hidden={isHidden}
  isFocus={isFocusOnPanelToggle}
  on:toggle={(e) => (isHidden = e.detail.isHidden)}>
  <form on:submit|preventDefault={formHandleSubmit}>
    <div class="Box-body">
      <div
        class="d-flex flex-justify-between flex-sm-nowrap flex-wrap mb-2 mb-sm-4">
        <div
          class="col-12 col-lg-8 mb-3 mb-sm-0 mr-sm-3 position-relative z-index-1">
          <GuestPicker />
        </div>
        <div class="col-12 mb-3 mb-sm-0 position-relative text-left z-index-0">
          <DateRangerPicker />
        </div>
      </div>

      {#if $isAllTooManyPeople || $isAllBooked}
        <AvailabilityStatus
          status={$availabilityEnableDateStatus}
          statusMessage={$availabilityEnableDateMessageStatus} />
      {:else if $isRejectedAvailabilityData}
        <AvailabilityStatus status="request-fail" />
      {:else if isEmptyLocationTypes}
        <AvailabilityStatus status="empty-fail" />
      {/if}

      <div class="d-flex flex-items-center">
        {#if onLoadWithParameters || $isLoadingAvailabilityData}
          <div
            class="d-inline-block mr-auto"
            data-testid="availability-loading"
            transition:fade>
            <img
              class="col-6 text-left"
              src="/assets/NPWS/images/loading.gif"
              alt="loading..." />
            <span class="sr-only">Loading...</span>
          </div>
        {/if}
        <button
          type="button"
          class="bttn bttn-outline bg-white mx-2 ml-auto"
          data-testid="availability-clear-all-button"
          on:click={clearAll}>Clear</button>

        {#if $isLoadingAvailabilityData}
          <button
            class="bttn bttn-primary"
            type="submit"
            disabled={$isLoadingAvailabilityData}
            data-testid="availability-submit-disabled-button">Submit</button>
        {:else}
          <button
            class="bttn bttn-primary"
            type="submit"
            {disabled}
            data-testid="availability-submit-button">Submit</button>
        {/if}
      </div>
    </div>
  </form>

  {#if $isRejectedAvailabilityData || !isEmptyLocationTypes}
    <ListingResult />
  {/if}
</ToggleVisibility>
